/**
 * Listeners List
 * View and manage listener runtimes (dev + prod)
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDefaultTenantId } from '../../lib/cpClient';
import { useAuthStore } from '../../store/authStore';
import { createListener, fetchListeners } from './api';
import { listenerEnvFromSession, resolveQueryTenant } from './tenantLabelHonesty';
import type { Listener } from './types';

export default function ListenersList() {
  const qc = useQueryClient();
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const queryTenant = resolveQueryTenant({
    selectedTenant,
    hostedTenant: getDefaultTenantId(),
  });
  const { data: listeners = [], isLoading, error } = useQuery({
    queryKey: ['iot', 'listeners'],
    queryFn: fetchListeners,
    refetchInterval: 15000,
  });

  const [newListenerId, setNewListenerId] = useState('');
  const [newEnv, setNewEnv] = useState(() => listenerEnvFromSession(queryTenant));
  const [newMode, setNewMode] = useState('http_webhook');
  const [newPort, setNewPort] = useState<string>('5050');

  useEffect(() => {
    setNewEnv(listenerEnvFromSession(queryTenant));
  }, [queryTenant]);

  const createMutation = useMutation({
    mutationFn: () =>
      createListener({
        listenerId: newListenerId.trim(),
        env: newEnv,
        mode: newMode,
        port: newMode === 'http_webhook' ? Number(newPort) : null,
        metadata: { name: newListenerId.trim() },
      } as any),
    onSuccess: () => {
      setNewListenerId('');
      qc.invalidateQueries({ queryKey: ['iot', 'listeners'] });
    },
  });

  const existingIds = useMemo(() => new Set(listeners.map((l) => l.listenerId)), [listeners]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load listeners: {String(error)}</p>
      </div>
    );
  }

  const online = listeners.filter((l) => (l.health?.status || 'unknown') === 'online');
  const canCreate = newListenerId.trim().length > 0 && !existingIds.has(newListenerId.trim());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Listeners</h1>
          <p className="text-gray-600 mt-1">
            {listeners.length} total • {online.length} online
          </p>
        </div>
      </div>

      {/* Create Listener (minimal) */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Listener ID</label>
            <input
              value={newListenerId}
              onChange={(e) => setNewListenerId(e.target.value)}
              placeholder="e.g. test-webhook-1"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            {!canCreate && newListenerId.trim().length > 0 && (
              <div className="text-xs text-red-700 mt-1">Listener ID already exists</div>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Env</label>
            <select
              value={newEnv}
              onChange={(e) => setNewEnv(e.target.value as 'dev' | 'prod')}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="dev">dev</option>
              <option value="prod">prod</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Mode</label>
            <select value={newMode} onChange={(e) => setNewMode(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="http_webhook">http_webhook</option>
              <option value="heartbeat">heartbeat</option>
              <option value="lorawan_webhook">lorawan_webhook</option>
              <option value="mqtt_bridge">mqtt_bridge</option>
              <option value="tcp">tcp</option>
              <option value="udp">udp</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Port</label>
            <input
              value={newPort}
              onChange={(e) => setNewPort(e.target.value)}
              disabled={newMode !== 'http_webhook'}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-50"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={() => createMutation.mutate()}
            disabled={!canCreate || createMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating…' : 'Create Listener'}
          </button>
          {createMutation.isError && <span className="text-sm text-red-700">Failed: {String(createMutation.error)}</span>}
          {createMutation.isSuccess && <span className="text-sm text-green-700">Created</span>}
        </div>
      </div>

      {listeners.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 8h8M8 12h8M8 16h6M6 4h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Listeners Found</h3>
          <p className="text-gray-600">Create a listener above, then run its command</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listener ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Env</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Heartbeat</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Run Command</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {listeners.map((l) => (
                <ListenerRow key={l._id} listener={l} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ListenerRow({ listener }: { listener: Listener }) {
  const status = (listener.health?.status || 'unknown') as 'online' | 'offline' | 'unknown';
  const statusColors: Record<string, string> = {
    online: 'bg-green-100 text-green-800',
    offline: 'bg-red-100 text-red-800',
    unknown: 'bg-gray-100 text-gray-800',
  };

  const lastHb = listener.health?.lastHeartbeatAt ? new Date(listener.health.lastHeartbeatAt).toLocaleString() : '—';

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[status] || statusColors.unknown}`}>
          {status}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">{listener.listenerId}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{listener.env || '—'}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{listener.mode || '—'}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lastHb}</td>
      <td className="px-6 py-4 whitespace-nowrap">
        {listener.runCommand ? (
          <code className="text-xs bg-gray-100 border border-gray-200 rounded px-2 py-1 inline-block max-w-[520px] truncate">
            {listener.runCommand}
          </code>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}

